import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CartItem, PaymentMethod, ProductDTO } from '@horology/shared-types';
import { listProducts } from '../products/products.api';
import { listWarehouses } from '../warehouses/warehouses.api';
import {
  cartTotals,
  checkout,
  createCustomer,
  issueInvoiceToDian,
  listSettleableOrders,
  searchCustomers,
  type SettleableOrder,
} from './pos.api';
import { formatCop, imageUrl } from '../../lib/format';
import { readApiError } from '../../lib/api';
import './pos.css';

type Tab = 'favorites' | 'all';

export function PosScreen() {
  const qc = useQueryClient();
  const searchRef = useRef<HTMLInputElement>(null);

  const [tab, setTab] = useState<Tab>('favorites');
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [warehouseId, setWarehouseId] = useState<number | null>(null);
  const [customer, setCustomer] = useState<{ id: number; label: string } | null>(null);
  const [showPay, setShowPay] = useState(false);
  const [showCustomerPicker, setShowCustomerPicker] = useState(false);
  const [showSettle, setShowSettle] = useState(false);
  const [settlingOs, setSettlingOs] = useState<SettleableOrder | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ message: string; invoiceId: number; dianStatus?: string } | null>(null);

  // Keep search input focused (helps with barcode scanners)
  useEffect(() => {
    const t = setTimeout(() => searchRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [showPay, showCustomerPicker, success]);

  const warehouses = useQuery({ queryKey: ['warehouses'], queryFn: listWarehouses });
  useEffect(() => {
    if (warehouseId == null && warehouses.data && warehouses.data.length > 0) {
      const def = warehouses.data.find((w) => w.isDefault) ?? warehouses.data[0];
      setWarehouseId(def?.id ?? null);
    }
  }, [warehouses.data, warehouseId]);

  const products = useQuery({
    queryKey: ['products', { q: search, favoritesOnly: tab === 'favorites' }],
    queryFn: () =>
      listProducts({
        q: search || undefined,
        favoritesOnly: tab === 'favorites',
        limit: 100,
      }),
    staleTime: 15_000,
  });

  const totals = useMemo(() => cartTotals(cart), [cart]);

  const addToCart = (p: ProductDTO) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.productId === p.id);
      if (existing) {
        return prev.map((i) =>
          i.productId === p.id ? { ...i, quantity: i.quantity + 1 } : i,
        );
      }
      return [
        ...prev,
        {
          productId: p.id,
          name: p.name,
          quantity: 1,
          unitPrice: Number(p.salePrice),
          taxRate: Number(p.taxRate),
          isService: p.isService,
          imagePath: p.imagePath,
          discount: 0,
        },
      ];
    });
  };

  const updateQty = (productId: number, q: number) =>
    setCart((prev) =>
      prev
        .map((i) => (i.productId === productId ? { ...i, quantity: Math.max(0, q) } : i))
        .filter((i) => i.quantity > 0),
    );

  const removeFromCart = (productId: number) =>
    setCart((prev) => prev.filter((i) => i.productId !== productId));

  const checkoutMut = useMutation({
    mutationFn: checkout,
    onSuccess: (r) => {
      setSuccess({
        message: `Venta ${r.invoiceNumber} registrada por ${formatCop(r.total)}`,
        invoiceId: r.invoiceId,
      });
      setCart([]);
      setShowPay(false);
      setCustomer(null);
      setSettlingOs(null);
      void qc.invalidateQueries({ queryKey: ['products'] });
      void qc.invalidateQueries({ queryKey: ['service-orders'] });
      void qc.invalidateQueries({ queryKey: ['kpis'] });

      // Auto-open ticket for printing
      const w = window.open(
        `/api/invoices/${r.invoiceId}/pdf?format=invoice-ticket`,
        '_blank',
        'noopener,noreferrer',
      );
      // Fall back gracefully if browser blocks the popup
      if (!w) {
        // Toast already shows the success; user can click "Imprimir" link.
      }
    },
    onError: (err) => setError(readApiError(err).message),
  });

  const dianMut = useMutation({
    mutationFn: issueInvoiceToDian,
    onSuccess: (r) =>
      setSuccess((prev) => (prev ? { ...prev, dianStatus: r.status } : null)),
    onError: (err) => setError(readApiError(err).message),
  });

  const importOsToCart = (os: SettleableOrder) => {
    // Replace cart with OS items (consider warning if cart non-empty)
    const newCart: CartItem[] = os.items.map((it) => ({
      productId: it.productId,
      name: it.description ?? it.product.name,
      quantity: Number(it.quantity),
      unitPrice: Number(it.unitPrice),
      taxRate: Number(it.taxRate),
      isService: it.product.isService || it.isLabor,
      imagePath: null,
      discount: 0,
    }));
    setCart(newCart);
    setSettlingOs(os);
    setCustomer({
      id: 0, // will be derived server-side from OS
      label: `${os.customer.firstName} ${os.customer.lastName} (OS ${os.folio})`,
    });
    setShowSettle(false);
  };

  return (
    <div className="pos">
      <section className="pos__catalog">
        <div className="pos__search">
          <input
            ref={searchRef}
            className="input"
            placeholder="Buscar por SKU o nombre... (lector de barras OK)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          {warehouses.data && warehouses.data.length > 1 && (
            <select
              className="input"
              style={{ width: 160 }}
              value={warehouseId ?? ''}
              onChange={(e) => setWarehouseId(Number(e.target.value))}
            >
              {warehouses.data.map((w) => (
                <option key={w.id} value={w.id}>{w.code}</option>
              ))}
            </select>
          )}
          <button className="btn" onClick={() => setShowSettle(true)} disabled={!!settlingOs}>
            Liquidar OS
          </button>
        </div>

        <div className="pos__tabs">
          <button
            className={`pos__tab ${tab === 'favorites' ? 'active' : ''}`}
            onClick={() => setTab('favorites')}
          >
            ★ Favoritos
          </button>
          <button
            className={`pos__tab ${tab === 'all' ? 'active' : ''}`}
            onClick={() => setTab('all')}
          >
            Todos
          </button>
        </div>

        {products.isLoading && <p>Cargando productos...</p>}
        {products.data && (
          <div className="pos__grid">
            {products.data.data.map((p) => (
              <div
                key={p.id}
                className={`pos__product ${p.isService ? 'pos__product--service' : ''}`}
                onClick={() => addToCart(p)}
                title={`${p.sku} - ${p.name}`}
              >
                <div
                  className="pos__product__img"
                  style={p.imagePath ? { backgroundImage: `url(${imageUrl(p.imagePath)})` } : undefined}
                >
                  {p.isService && !p.imagePath && '🛠'}
                </div>
                <div className="pos__product__name">{p.name}</div>
                <div className="pos__product__price">{formatCop(p.salePrice)}</div>
              </div>
            ))}
            {products.data.data.length === 0 && (
              <p style={{ color: 'var(--color-muted)' }}>Sin resultados.</p>
            )}
          </div>
        )}
      </section>

      <aside className="pos__cart">
        <div
          className="pos__cart__header"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            ...(settlingOs && { background: '#fef3c7' }),
          }}
        >
          <span>
            {settlingOs ? `Liquidando OS ${settlingOs.folio}` : `Carrito (${cart.length})`}
          </span>
          {!settlingOs && (
            <button
              className="btn"
              style={{ padding: '0.25rem 0.6rem', fontSize: '0.8rem' }}
              onClick={() => setShowCustomerPicker(true)}
            >
              {customer ? customer.label : '+ Cliente'}
            </button>
          )}
          {settlingOs && (
            <button
              className="btn"
              style={{ padding: '0.25rem 0.6rem', fontSize: '0.8rem' }}
              onClick={() => {
                setSettlingOs(null);
                setCustomer(null);
                setCart([]);
              }}
            >
              Cancelar liquidación
            </button>
          )}
        </div>

        <div className="pos__cart__lines">
          {cart.length === 0 && (
            <p style={{ padding: '1rem', color: 'var(--color-muted)', fontSize: '0.875rem' }}>
              Toca un producto para agregarlo.
            </p>
          )}
          {cart.map((line) => (
            <div className="pos__line" key={line.productId}>
              <div>
                <div className="pos__line__top">
                  <span style={{ fontWeight: 500 }}>{line.name}</span>
                  <span>{formatCop(line.unitPrice * line.quantity - line.discount)}</span>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--color-muted)' }}>
                  {formatCop(line.unitPrice)} × {line.quantity}
                </div>
                <div className="pos__line__qty" style={{ marginTop: '0.25rem' }}>
                  <button onClick={() => updateQty(line.productId, line.quantity - 1)}>-</button>
                  <input
                    type="number"
                    min={0}
                    step="0.001"
                    value={line.quantity}
                    onChange={(e) => updateQty(line.productId, Number(e.target.value))}
                    style={{ width: 64, textAlign: 'center' }}
                  />
                  <button onClick={() => updateQty(line.productId, line.quantity + 1)}>+</button>
                </div>
              </div>
              <button className="pos__line__remove" onClick={() => removeFromCart(line.productId)}>×</button>
            </div>
          ))}
        </div>

        <div className="pos__totals">
          <div className="pos__totals__row"><span>Subtotal</span><span>{formatCop(totals.subtotal)}</span></div>
          <div className="pos__totals__row"><span>IVA</span><span>{formatCop(totals.taxTotal)}</span></div>
          <div className="pos__totals__row pos__totals__total"><span>Total</span><span>{formatCop(totals.total)}</span></div>
        </div>

        <div className="pos__pay">
          <button
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={cart.length === 0 || !warehouseId}
            onClick={() => setShowPay(true)}
          >
            Cobrar {formatCop(totals.total)}
          </button>
        </div>
      </aside>

      {showPay && warehouseId && (
        <PaymentModal
          total={totals.total}
          onCancel={() => setShowPay(false)}
          onConfirm={(payments) => {
            setError(null);
            checkoutMut.mutate({
              customerId: settlingOs ? undefined : customer?.id || undefined,
              serviceOrderId: settlingOs?.id,
              warehouseId,
              items: cart.map((i) => ({
                productId: i.productId,
                quantity: i.quantity,
                unitPriceOverride: i.unitPrice,
                discount: i.discount || undefined,
              })),
              payments,
            });
          }}
          submitting={checkoutMut.isPending}
          error={error}
        />
      )}

      {showCustomerPicker && (
        <CustomerPicker
          onCancel={() => setShowCustomerPicker(false)}
          onSelect={(c) => {
            setCustomer({ id: c.id, label: `${c.firstName} ${c.lastName}` });
            setShowCustomerPicker(false);
          }}
        />
      )}

      {showSettle && (
        <SettleOsPicker
          onCancel={() => setShowSettle(false)}
          onSelect={importOsToCart}
        />
      )}

      {success && (
        <div
          style={{
            position: 'fixed',
            bottom: 16,
            left: 16,
            background: 'var(--color-primary)',
            color: '#fff',
            padding: '0.75rem 1rem',
            borderRadius: 'var(--radius)',
            zIndex: 200,
            display: 'flex',
            gap: '0.75rem',
            alignItems: 'center',
            maxWidth: 480,
          }}
        >
          <span>{success.message}</span>
          {success.dianStatus ? (
            <span style={{ background: 'rgba(201,162,39,0.3)', padding: '2px 8px', borderRadius: 4, fontSize: '0.8rem' }}>
              DIAN: {success.dianStatus}
            </span>
          ) : (
            <button
              className="btn"
              style={{ padding: '4px 10px', fontSize: '0.8rem' }}
              disabled={dianMut.isPending}
              onClick={() => dianMut.mutate(success.invoiceId)}
            >
              {dianMut.isPending ? 'Emitiendo...' : 'Emitir DIAN'}
            </button>
          )}
          <button
            className="btn"
            style={{ padding: '4px 10px', fontSize: '0.8rem' }}
            onClick={() => setSuccess(null)}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

function PaymentModal(props: {
  total: number;
  onCancel: () => void;
  onConfirm: (payments: { method: PaymentMethod; amount: number; reference?: string }[]) => void;
  submitting: boolean;
  error: string | null;
}) {
  const [method, setMethod] = useState<PaymentMethod>('CASH');
  const [amount, setAmount] = useState(String(props.total));
  const [reference, setReference] = useState('');

  const numericAmount = Number(amount) || 0;
  const change = numericAmount - props.total;

  return (
    <div style={modalBackdrop}>
      <div className="card" style={{ width: 420 }}>
        <h3 style={{ marginTop: 0 }}>Cobrar {formatCop(props.total)}</h3>

        <div className="field">
          <label>Método</label>
          <select className="input" value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
            <option value="CASH">Efectivo</option>
            <option value="CARD">Tarjeta</option>
            <option value="TRANSFER">Transferencia</option>
            <option value="OTHER">Otro</option>
          </select>
        </div>
        <div className="field">
          <label>Monto recibido</label>
          <input
            className="input"
            type="number"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            autoFocus
          />
        </div>
        {(method === 'CARD' || method === 'TRANSFER' || method === 'OTHER') && (
          <div className="field">
            <label>Referencia (opcional)</label>
            <input className="input" value={reference} onChange={(e) => setReference(e.target.value)} />
          </div>
        )}

        {method === 'CASH' && change > 0 && (
          <p>Cambio: <strong>{formatCop(change)}</strong></p>
        )}
        {props.error && <p className="error">{props.error}</p>}

        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
          <button
            className="btn btn-primary"
            disabled={props.submitting || numericAmount < props.total}
            onClick={() =>
              props.onConfirm([{ method, amount: props.total, reference: reference || undefined }])
            }
            style={{ flex: 1 }}
          >
            {props.submitting ? 'Procesando...' : 'Confirmar'}
          </button>
          <button className="btn" onClick={props.onCancel}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function CustomerPicker(props: {
  onCancel: () => void;
  onSelect: (c: { id: number; firstName: string; lastName: string }) => void;
}) {
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ idDocument: '', firstName: '', lastName: '', email: '', phone: '' });
  const [error, setError] = useState<string | null>(null);

  const search = useQuery({
    queryKey: ['customers-search', q],
    queryFn: () => searchCustomers(q),
    enabled: q.length >= 2,
  });

  const createMut = useMutation({
    mutationFn: createCustomer,
    onSuccess: (c) => props.onSelect(c),
    onError: (err) => setError(readApiError(err).message),
  });

  return (
    <div style={modalBackdrop}>
      <div className="card" style={{ width: 480, maxHeight: '80vh', overflowY: 'auto' }}>
        <h3 style={{ marginTop: 0 }}>Seleccionar cliente</h3>
        {!creating ? (
          <>
            <input
              className="input"
              placeholder="Buscar por cédula, nombre, teléfono..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              autoFocus
            />
            <div style={{ marginTop: '0.5rem', maxHeight: 220, overflowY: 'auto' }}>
              {search.data?.map((c) => (
                <button
                  key={c.id}
                  className="btn"
                  style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: '0.25rem' }}
                  onClick={() => props.onSelect(c)}
                >
                  {c.firstName} {c.lastName} — <small>{c.idDocument}</small>
                </button>
              ))}
              {search.data && search.data.length === 0 && q.length >= 2 && (
                <p style={{ color: 'var(--color-muted)', fontSize: '0.85rem' }}>Sin resultados.</p>
              )}
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <button className="btn btn-primary" onClick={() => setCreating(true)}>+ Nuevo cliente</button>
              <button className="btn" onClick={props.onCancel}>Cancelar</button>
            </div>
          </>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              createMut.mutate(form);
            }}
          >
            <div className="field">
              <label>Cédula</label>
              <input className="input" value={form.idDocument} onChange={(e) => setForm({ ...form, idDocument: e.target.value })} required />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              <div className="field">
                <label>Nombre</label>
                <input className="input" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} required />
              </div>
              <div className="field">
                <label>Apellido</label>
                <input className="input" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} required />
              </div>
            </div>
            <div className="field">
              <label>Email</label>
              <input className="input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </div>
            <div className="field">
              <label>Teléfono</label>
              <input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
            </div>
            {error && <p className="error">{error}</p>}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="submit" className="btn btn-primary" disabled={createMut.isPending}>
                {createMut.isPending ? 'Creando...' : 'Crear y seleccionar'}
              </button>
              <button type="button" className="btn" onClick={() => setCreating(false)}>Volver</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const modalBackdrop: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'grid',
  placeItems: 'center',
  zIndex: 100,
};

function SettleOsPicker(props: {
  onCancel: () => void;
  onSelect: (os: SettleableOrder) => void;
}) {
  const [q, setQ] = useState('');
  const orders = useQuery({
    queryKey: ['settleable-orders', q],
    queryFn: () => listSettleableOrders(q || undefined),
  });

  return (
    <div style={modalBackdrop}>
      <div className="card" style={{ width: 600, maxHeight: '80vh', overflowY: 'auto' }}>
        <h3 style={{ marginTop: 0 }}>Liquidar Orden de Servicio</h3>
        <p style={{ color: 'var(--color-muted)', marginTop: 0, fontSize: '0.85rem' }}>
          Seleccionando una OS se importan sus items al carrito y al cobrar se marca como Entregada.
        </p>
        <input
          className="input"
          placeholder="Buscar por folio, cédula o apellido..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
        />
        <div style={{ marginTop: '0.75rem' }}>
          {orders.isLoading && <p>Cargando...</p>}
          {orders.data?.length === 0 && (
            <p style={{ color: 'var(--color-muted)' }}>
              No hay órdenes en estado Listo o Abandonado para liquidar.
            </p>
          )}
          {orders.data?.map((o) => {
            const itemsTotal = o.items.reduce(
              (s, i) => s + Number(i.quantity) * Number(i.unitPrice),
              0,
            );
            return (
              <button
                key={o.id}
                className="btn"
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  marginBottom: '0.4rem',
                  padding: '0.6rem 0.8rem',
                }}
                onClick={() => props.onSelect(o)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <strong>{o.folio}</strong>
                  <span>{formatCop(itemsTotal || Number(o.estimatedValue))}</span>
                </div>
                <small style={{ color: 'var(--color-muted)' }}>
                  {o.customer.firstName} {o.customer.lastName} · {o.customer.idDocument}
                  {' · '}
                  {o.items.length} items · {o.state}
                </small>
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
          <button className="btn" onClick={props.onCancel}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}
